import React from 'react';
import {
  type AcademyOperatingProfileInput,
  type ClubOperatingProfileInput,
  type OrgOperationType,
  type RacquetSportId,
  RACQUET_SPORT_IDS,
  RACQUET_SPORT_LABELS,
} from '@court-sync/shared';
import { Input, Field, FieldLabel } from '@/components/ui';
import { SportIconTile } from '@/components/sport/SportIcons';
import { SelectableCard } from './SelectableCard';

function toggleAcademySport(
  profile: Partial<AcademyOperatingProfileInput>,
  sportId: RacquetSportId,
): Partial<AcademyOperatingProfileInput> {
  const sports = [...(profile.sports ?? [])];
  const existingIndex = sports.findIndex((s) => s.sportId === sportId);
  if (existingIndex >= 0) {
    sports.splice(existingIndex, 1);
    if (sports.length > 0 && !sports.some((s) => s.isPrimary)) {
      sports[0] = { ...sports[0], isPrimary: true };
    }
    return { ...profile, sports };
  }
  const isFirst = sports.length === 0;
  sports.push({ sportId, isPrimary: isFirst });
  return { ...profile, sports };
}

function setAcademyPrimarySport(
  profile: Partial<AcademyOperatingProfileInput>,
  sportId: RacquetSportId,
): Partial<AcademyOperatingProfileInput> {
  const sports = (profile.sports ?? []).map((s) => ({
    ...s,
    isPrimary: s.sportId === sportId,
  }));
  return { ...profile, sports };
}

function toggleClubSport(
  profile: Partial<ClubOperatingProfileInput>,
  sportId: RacquetSportId,
): Partial<ClubOperatingProfileInput> {
  const sports = [...(profile.sports ?? [])];
  const existingIndex = sports.findIndex((s) => s.sportId === sportId);
  if (existingIndex >= 0) {
    sports.splice(existingIndex, 1);
    if (sports.length > 0 && !sports.some((s) => s.isPrimary)) {
      sports[0] = { ...sports[0], isPrimary: true };
    }
    return { ...profile, sports };
  }
  const isFirst = sports.length === 0;
  sports.push({ sportId, isPrimary: isFirst });
  return { ...profile, sports };
}

function setClubPrimarySport(
  profile: Partial<ClubOperatingProfileInput>,
  sportId: RacquetSportId,
): Partial<ClubOperatingProfileInput> {
  const sports = (profile.sports ?? []).map((s) => ({
    ...s,
    isPrimary: s.sportId === sportId,
  }));
  return { ...profile, sports };
}

export function hasValidFastTrackSports(
  operationType: OrgOperationType,
  operatingProfile: Partial<AcademyOperatingProfileInput>,
  clubOperatingProfile: Partial<ClubOperatingProfileInput>,
): boolean {
  const sports =
    operationType === 'club' ? clubOperatingProfile.sports : operatingProfile.sports;
  if (!sports?.length) return false;
  if (sports.filter((s) => s.isPrimary).length !== 1) return false;
  const other = sports.find((s) => s.sportId === 'other');
  if (other && !other.customLabel?.trim()) return false;
  return true;
}

interface FastTrackSportsPickerProps {
  operationType: OrgOperationType;
  operatingProfile: Partial<AcademyOperatingProfileInput>;
  clubOperatingProfile: Partial<ClubOperatingProfileInput>;
  onOperatingProfileChange: (profile: Partial<AcademyOperatingProfileInput>) => void;
  onClubProfileChange: (profile: Partial<ClubOperatingProfileInput>) => void;
}

export function FastTrackSportsPicker({
  operationType,
  operatingProfile,
  clubOperatingProfile,
  onOperatingProfileChange,
  onClubProfileChange,
}: FastTrackSportsPickerProps) {
  const isClubOnly = operationType === 'club';
  const selectedSports = isClubOnly
    ? (clubOperatingProfile.sports ?? [])
    : (operatingProfile.sports ?? []);
  const otherSport = selectedSports.find((s) => s.sportId === 'other');

  const handleToggle = (sportId: RacquetSportId) => {
    if (isClubOnly) {
      onClubProfileChange(toggleClubSport(clubOperatingProfile, sportId));
      return;
    }
    onOperatingProfileChange(toggleAcademySport(operatingProfile, sportId));
  };

  const handleSetPrimary = (sportId: RacquetSportId) => {
    if (isClubOnly) {
      onClubProfileChange(setClubPrimarySport(clubOperatingProfile, sportId));
      return;
    }
    onOperatingProfileChange(setAcademyPrimarySport(operatingProfile, sportId));
  };

  const handleOtherLabelChange = (value: string) => {
    const updateSports = (sports: typeof selectedSports) =>
      sports.map((s) => (s.sportId === 'other' ? { ...s, customLabel: value } : s));

    if (isClubOnly) {
      onClubProfileChange({
        ...clubOperatingProfile,
        sports: updateSports(clubOperatingProfile.sports ?? []),
      });
      return;
    }
    onOperatingProfileChange({
      ...operatingProfile,
      sports: updateSports(operatingProfile.sports ?? []),
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          Which racquet sports do you offer?
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select all that apply. Choose one as your primary sport.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label="Sports offered">
        {RACQUET_SPORT_IDS.map((sportId) => {
          const selected = selectedSports.some((s) => s.sportId === sportId);
          const isPrimary = selectedSports.find((s) => s.sportId === sportId)?.isPrimary;
          return (
            <div key={sportId} className="space-y-2">
              <SelectableCard
                id={`fast-track-sport-${sportId}`}
                title={RACQUET_SPORT_LABELS[sportId]}
                icon={<SportIconTile sportId={sportId} />}
                iconContainerClassName="contents"
                badge={isPrimary ? 'Primary' : undefined}
                selected={selected}
                onSelect={() => handleToggle(sportId)}
              />
              {selected && selectedSports.length > 1 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                  onClick={() => handleSetPrimary(sportId)}
                >
                  {isPrimary ? 'Primary sport' : 'Set as primary'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {otherSport ? (
        <Field>
          <FieldLabel htmlFor="fast-track-other-sport-label">Name for other sport</FieldLabel>
          <Input
            id="fast-track-other-sport-label"
            value={otherSport.customLabel ?? ''}
            onChange={(event) => handleOtherLabelChange(event.target.value)}
            placeholder="e.g. Racquetball"
            maxLength={80}
          />
        </Field>
      ) : null}
    </div>
  );
}
